import { PreferredScheduleConfig as PreferredScheduleConfigModel } from '@models/product';
import { Button, Select } from 'antd';
import React from 'react';
import './PreferredScheduleConfig.scss';

const MINUTE_OPTIONS = ['00', '30'] as const;
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, index) => index);

export const DEFAULT_PREFERRED_SCHEDULE: PreferredScheduleConfigModel = {
  startTime: '',
  endTime: '',
  intervalMinutes: 30,
};

const formatTimeValue = (hour: number, minute: number) =>
  `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

const parseTimeValue = (value: string) => {
  const match = value.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return null;
  }

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour < 0 || hour > 23 || ![0, 30].includes(minute)) {
    return null;
  }

  return { hour, minute };
};

const TIME_OPTIONS = HOUR_OPTIONS.flatMap((hour) =>
  MINUTE_OPTIONS.map((minute) => formatTimeValue(hour, Number(minute))),
);

interface PreferredScheduleConfigProps {
  value?: PreferredScheduleConfigModel;
  onChange: (value: PreferredScheduleConfigModel) => void;
}

export const PreferredScheduleConfigPanel: React.FC<PreferredScheduleConfigProps> = (props) => {
  const config = {
    ...DEFAULT_PREFERRED_SCHEDULE,
    ...props.value,
  };

  const updateConfig = (partial: Partial<PreferredScheduleConfigModel>) => {
    props.onChange({ ...config, ...partial });
  };

  const handleStartTimeChange = (nextStartTime: string) => {
    const start = parseTimeValue(nextStartTime);
    const end = parseTimeValue(config.endTime);
    if (start && end && start.hour * 60 + start.minute >= end.hour * 60 + end.minute) {
      return;
    }
    updateConfig({ startTime: nextStartTime });
  };

  const handleEndTimeChange = (nextEndTime: string) => {
    const start = parseTimeValue(config.startTime);
    const end = parseTimeValue(nextEndTime);
    if (start && end && start.hour * 60 + start.minute >= end.hour * 60 + end.minute) {
      return;
    }
    updateConfig({ endTime: nextEndTime });
  };

  return (
    <div className="preferred-schedule-config">
      <div className="preferred-schedule-config__panel">
        <div className="preferred-schedule-config__row">
          <div className="preferred-schedule-config__row-label">운영 시간</div>
          <div className="preferred-schedule-config__hours">
            <span className="preferred-schedule-config__hours-label">시작</span>
            <Select
              allowClear
              placeholder="00:00"
              style={{ width: 88 }}
              value={config.startTime || undefined}
              options={TIME_OPTIONS.map((option) => ({ label: option, value: option }))}
              onChange={(value) => handleStartTimeChange(value || '')}
            />
            <span className="preferred-schedule-config__hours-separator">-</span>
            <span className="preferred-schedule-config__hours-label">마지막</span>
            <Select
              allowClear
              placeholder="00:00"
              style={{ width: 88 }}
              value={config.endTime || undefined}
              options={TIME_OPTIONS.map((option) => ({ label: option, value: option }))}
              onChange={(value) => handleEndTimeChange(value || '')}
            />
          </div>
        </div>
        <div className="preferred-schedule-config__row">
          <div className="preferred-schedule-config__row-label">시간 간격</div>
          <div className="preferred-schedule-config__intervals">
            <Button
              type={config.intervalMinutes === 30 ? 'primary' : 'default'}
              className={
                config.intervalMinutes === 30
                  ? 'preferred-schedule-config__interval-btn preferred-schedule-config__interval-btn--active'
                  : 'preferred-schedule-config__interval-btn'
              }
              onClick={() => updateConfig({ intervalMinutes: 30 })}>
              30분 단위
            </Button>
            <Button
              type={config.intervalMinutes === 60 ? 'primary' : 'default'}
              className={
                config.intervalMinutes === 60
                  ? 'preferred-schedule-config__interval-btn preferred-schedule-config__interval-btn--active'
                  : 'preferred-schedule-config__interval-btn'
              }
              onClick={() => updateConfig({ intervalMinutes: 60 })}>
              1시간 단위
            </Button>
          </div>
        </div>
      </div>
      <div className="preferred-schedule-config__helper">
        희망 일정을 설정하면, 고객이 구매 시 달력과 시간 버튼으로 옵션을 선택할 수 있습니다.
      </div>
    </div>
  );
};
